import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Heart, Users, PenTool, MessageSquare, ArrowRight, Sparkles } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { EditorCard } from "@/components/EditorCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const GENRES = ["Narrativa", "Thriller", "Saggistica", "Fantasy", "Poesia", "Giallo"];
const HERO = "https://images.unsplash.com/photo-1576479854195-52883a461a68?crop=entropy&cs=srgb&fm=jpg&q=85";

const FADE_LEFT = { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } };
const FADE_IMG = { initial: { opacity: 0, scale: 0.96 }, animate: { opacity: 1, scale: 1 }, transition: { duration: 0.8, delay: 0.15 } };
const STEPS = [
    { icon: Search, t: "1. Cerca", d: "Filtra gli editor per fascia di prezzo, località, genere letterario e recensioni positive." },
    { icon: MessageSquare, t: "2. Richiedi un preventivo", d: "Consulta bio, portfolio, tariffe e contatta l'editor direttamente per un preventivo gratuito." },
    { icon: PenTool, t: "3. Pubblica il tuo libro", d: "Lavora con il professionista giusto e porta il tuo manoscritto al livello successivo." },
];

export default function Home() {
    const [editors, setEditors] = useState([]);
    const [q, setQ] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/editors", { params: { sort: "reviews" } }).then(({ data }) => setEditors(data.slice(0, 3)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submitSearch = (e) => {
        e.preventDefault();
        navigate(`/editors?q=${encodeURIComponent(q)}`);
    };

    return (
        <div>
            {/* HERO */}
            <section className="relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center py-16 md:py-24">
                    <motion.div {...FADE_LEFT}>
                        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-medium text-primary mb-5">
                            <Sparkles size={14} /> 100% gratuito · no-profit
                        </span>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium tracking-tight leading-[1.05]">
                            Trova l'editor perfetto per il tuo <span className="text-primary italic">romanzo</span>
                        </h1>
                        <p className="text-base sm:text-lg text-muted-foreground mt-6 max-w-lg leading-relaxed">
                            Incipit connette scrittori emergenti con i migliori editor freelance d'Italia.
                            Cerca per prezzo, località e recensioni. Gratis, per sempre.
                        </p>
                        <form onSubmit={submitSearch} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg" data-testid="hero-search-form">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                                <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="hero-search-input"
                                    placeholder="Cerca per nome, genere o stile…" className="pl-10 h-12 bg-card" />
                            </div>
                            <Button type="submit" size="lg" className="h-12 bg-primary hover:bg-primary/90 gap-2" data-testid="hero-search-btn">
                                Cerca <ArrowRight size={18} />
                            </Button>
                        </form>
                        <div className="flex flex-wrap gap-2 mt-5">
                            {GENRES.map((g) => (
                                <Link key={g} to={`/editors?genre=${encodeURIComponent(g)}`} data-testid={`hero-genre-${g}`}
                                    className="text-xs px-3 py-1.5 rounded-full border border-border hover:border-primary hover:text-primary transition-colors">
                                    {g}
                                </Link>
                            ))}
                        </div>
                    </motion.div>

                    <motion.div {...FADE_IMG} className="relative h-[380px] lg:h-[520px]">
                        <div className="absolute inset-0 rounded-2xl overflow-hidden shadow-2xl">
                            <img src={HERO} alt="Biblioteca" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                        </div>
                        <div className="absolute -bottom-6 -left-6 bg-card border border-border rounded-xl p-5 shadow-xl hidden sm:block">
                            <p className="font-mono-metric text-3xl font-semibold text-primary">6+</p>
                            <p className="text-sm text-muted-foreground">editor verificati</p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* FEATURED */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <h2 className="text-3xl sm:text-4xl font-serif font-medium">Editor in evidenza</h2>
                        <p className="text-muted-foreground mt-2">I professionisti più apprezzati dalla comunità.</p>
                    </div>
                    <Link to="/editors"><Button variant="outline" className="gap-2 hidden sm:flex" data-testid="see-all-editors">Vedi tutti <ArrowRight size={16} /></Button></Link>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                    {editors.map((e, i) => <EditorCard key={e.editor_id} editor={e} index={i} />)}
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section id="come-funziona" className="bg-paper border-y border-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <h2 className="text-3xl sm:text-4xl font-serif font-medium text-center">Come funziona</h2>
                    <p className="text-muted-foreground mt-3 text-center max-w-xl mx-auto">Semplice, trasparente e senza costi.</p>
                    <div className="grid md:grid-cols-3 gap-8 mt-14">
                        {STEPS.map((s) => (
                            <div key={s.t} className="text-center">
                                <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto mb-5">
                                    <s.icon size={24} strokeWidth={1.6} />
                                </div>
                                <h3 className="font-serif text-2xl font-medium">{s.t}</h3>
                                <p className="text-muted-foreground mt-2 leading-relaxed">{s.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PHILOSOPHY */}
            <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
                <Heart className="text-primary mx-auto mb-6" size={36} strokeWidth={1.5} />
                <h2 className="text-3xl sm:text-4xl font-serif font-medium leading-tight">Un servizio libero, per chi ama i libri</h2>
                <p className="text-lg text-muted-foreground mt-6 leading-relaxed">
                    Incipit non è un business. È uno spazio gratuito sia per gli scrittori sia per gli editor,
                    nato per rendere l'editoria più accessibile e trasparente. Nessuna commissione, nessun abbonamento.
                </p>
                <div className="flex flex-wrap justify-center gap-4 mt-10">
                    <Link to="/editors"><Button size="lg" className="bg-primary hover:bg-primary/90 gap-2" data-testid="cta-find-editor"><Users size={18} /> Trova un editor</Button></Link>
                    <Link to="/auth?mode=register"><Button size="lg" variant="outline" className="gap-2" data-testid="cta-become-editor"><PenTool size={18} /> Diventa editor</Button></Link>
                </div>
            </section>
        </div>
    );
}
